import { AlertTriangle, Images } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

import type { FramingArtifact } from "./framingInspector";
import {
  isFramingContactSheetArtifact,
  selectCurrentFramingContactSheets,
} from "./framingViewUtils";
import { formatDateTime } from "./sessionUtils";

function shortHash(value?: string | null): string {
  if (!value) return "hash not recorded";
  return value.length > 20 ? `${value.slice(0, 12)}…${value.slice(-6)}` : value;
}

export function FramingContactSheets({
  artifacts,
  attemptId,
  taskId,
  planDigest,
}: {
  artifacts: FramingArtifact[];
  attemptId: string | null;
  taskId: string | null;
  planDigest: string | null;
}) {
  const candidates = artifacts.filter(isFramingContactSheetArtifact);
  if (candidates.length === 0) return null;

  const contactSheets = selectCurrentFramingContactSheets(artifacts, {
    attemptId,
    taskId,
    planDigest,
  });
  if (contactSheets.length === 0) {
    return (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>Contact-sheet evidence withheld</AlertTitle>
        <AlertDescription>
          No retained sheet exactly matched the current attempt, plan digest,
          and provider task. A stale or uncorrelated diagnostic is never shown
          as proof for this scene.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <section
      className="space-y-3 rounded-md border p-3"
      aria-label="Framing contact sheets"
    >
      <div>
        <h4 className="flex items-center gap-2 text-sm font-medium">
          <Images className="size-4" />
          Delivered-pixel contact sheets
        </h4>
        <p className="mt-1 text-xs text-muted-foreground">
          Composite diagnostics are displayed separately from coordinate-space
          overlays. Their boxes are baked into the retained image.
        </p>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {contactSheets.map((artifact) => {
          const hypothetical = artifact.kind === "framing_hypothetical_preview";
          return (
            <figure
              key={artifact.id}
              className="overflow-hidden rounded-md border"
            >
              <div className="flex flex-wrap items-center gap-2 border-b p-2 text-xs">
                <Badge variant={hypothetical ? "secondary" : "outline"}>
                  {hypothetical
                    ? "Hypothetical enforce preview"
                    : "Actual applied evidence"}
                </Badge>
                <span className="text-muted-foreground">
                  {hypothetical ? "not applied" : "rendered pixels"}
                </span>
              </div>
              <div className="bg-black p-2">
                <img
                  src={artifact.url ?? undefined}
                  alt={
                    hypothetical
                      ? "Hypothetical framing contact sheet; not applied"
                      : "Actual applied framing contact sheet"
                  }
                  className="max-h-[32rem] w-full object-contain"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </div>
              <figcaption className="space-y-1 p-2 font-mono text-[10px] text-muted-foreground">
                <div>{shortHash(artifact.sha256)}</div>
                <div>
                  {artifact.retentionClass ?? "retention not recorded"}
                  {artifact.expiresAt
                    ? ` · expires ${formatDateTime(artifact.expiresAt)}`
                    : ""}
                </div>
              </figcaption>
            </figure>
          );
        })}
      </div>
    </section>
  );
}
